import React, { useEffect, useLayoutEffect } from "react"
import { Box, ButtonLink, EmptyState, EmptyStateTitle, Text } from "@indeed/ifl-components"
import { InternalServerError1 } from "@indeed/indeed-design-system-illustrations-hero"
import { configSelector } from "../../common/utils/config-slice"
import { logDatadogRumMessage } from "../../common/utils/datadog"
import { useI18n } from "../../common/utils/i18n-context"
import { useAppSelector } from "../../common/utils/redux-helpers"
import { displayFooter } from "../../common/utils/utils"

export interface ErrorPageProps {
    header?: string
    subHeader?: string
    datadogRumMessage?: string
    originalException?: Error
    icon?: JSX.Element
    hidePreferencesButton?: boolean
    pageStylingOverride?: Record<string, string | number>
    headerStylingOverride?: Record<string, string | number>
    subheaderStylingOverride?: Record<string, string | number>
    svgStylingOverride?: Record<string, string | number>
}

export const ErrorPage: React.FC<ErrorPageProps> = (props: ErrorPageProps) => {
    const { gettext } = useI18n()
    const {
        header = gettext("Something went wrong on our end"),
        subHeader = gettext("We're working to fix it. Please try again later."),
        datadogRumMessage = header,
        originalException,
        icon = <InternalServerError1 data-testid="server-error-icon" />,
        hidePreferencesButton = false,
        pageStylingOverride = {},
        headerStylingOverride = {},
        subheaderStylingOverride = {},
        svgStylingOverride = {},
    } = props
    const config = useAppSelector(configSelector)

    useLayoutEffect(() => {
        displayFooter()
    }, [])

    useEffect(() => {
        try {
            logDatadogRumMessage(datadogRumMessage, originalException ? { originalException } : undefined)
        } catch (err) {
            console.error("Failed to log to datadog", err)
        }
    }, [])

    return (
        <EmptyState
            illustration={icon}
            actions={[
                <ButtonLink key="findJobs" href={config.jobSearchUrl} variant={hidePreferencesButton ? "primary" : "secondary"}>
                    {gettext("Find jobs")}
                </ButtonLink>,
                ...(hidePreferencesButton
                    ? []
                    : [
                          <ButtonLink key="preferences" href={`${config.profileUrl}/preferences`}>
                              {gettext("Manage job preferences")}
                          </ButtonLink>,
                      ]),
            ]}
            sx={{
                marginBlockEnd: 0,
                marginBlockStart: 8,
                marginInlineEnd: "auto",
                marginInlineStart: "auto",
                maxInlineSize: "50rem",
                paddingBlockEnd: 4,
                paddingBlockStart: 4,
                paddingInlineEnd: 4,
                paddingInlineStart: 4,
                svg: {
                    maxInlineSize: "9.375rem !important",
                    ...svgStylingOverride,
                },
                ...pageStylingOverride,
            }}
        >
            <Box sx={{ marginBlockEnd: 5 }}>
                <EmptyStateTitle
                    as="h1"
                    sx={{
                        fontSize: 4,
                        lineHeight: "loose",
                        paddingBlockEnd: 2,
                        paddingBlockStart: 0,
                        paddingInlineEnd: 7,
                        paddingInlineStart: 7,
                        ...headerStylingOverride,
                    }}
                >
                    {header}
                </EmptyStateTitle>
                <Text level={2} sx={{ display: "block", ...subheaderStylingOverride }}>
                    {subHeader}
                </Text>
            </Box>
        </EmptyState>
    )
}
